// Phase 28 (item G / CK1) — browser SSE client with manual reconnect.
//
// EventSource cannot send an Authorization header, and each stream ticket is
// single-use + short-lived, so the browser's built-in reconnect (which reuses
// the same consumed ticket URL) would 401. We manage reconnection ourselves:
// mint a fresh ticket (which itself requires a still-valid JWT — so a dead
// token can't sustain a stream), open, and on error re-mint + reopen with
// ?lastEventId= so the server replays missed events through its per-subscriber
// access filter. Returns a stop() function.
//
// Round 14 (C84) — STALL WATCHDOG + pop-out-hosted timers. Two failure modes
// used to leave the app on a zombie stream with no error event ever firing:
// (a) a half-dead TCP connection (proxy/NAT drop) keeps readyState OPEN
// forever; (b) Chrome throttles/freezes a BACKGROUNDED main window's timers
// — exactly the state the user is in while working the always-visible
// pop-out column — so even a detected drop could wait minutes to reconnect.
// The server now sends a real `hb` event every 25s; we track the last
// receive instant and force a close + re-mint when it goes quiet for
// STALL_MS. The watchdog (and the reconnect timer) is armed on the MAIN
// window AND, when the caller provides one via getTimerHost(), on the
// POP-OUT window — a Document-PiP window is always visible, so its timers
// never throttle and the pipeline stays live while the main window sleeps.
// Reconnects pass lastEventId, so the hub's ring replay delivers whatever
// the stall swallowed: the pop-out catches up without a refresh.

import { mintStreamTicket, API_URL } from "../api";

const STALL_MS = 70000; // ~3 missed heartbeats
const WATCH_EVERY_MS = 20000;

type StreamHandler = (data: any) => void;
type StartLiveStreamOpts = {
  onChat?: StreamHandler;
  onNotification?: StreamHandler;
  onTask?: StreamHandler;
  onSubtask?: StreamHandler;
  onAgenda?: StreamHandler;
  onChatRead?: StreamHandler;
  onStatus?: (status: string) => void;
  handlers?: Record<string, StreamHandler>;
  getTimerHost?: () => any;
};

export function startLiveStream({
  onChat,
  onNotification,
  // Phase 29 (item 5) — task / subtask / agenda mutations and read-receipt
  // writes now stream too (server routes publish; the hub's per-subscriber
  // gate scopes delivery).
  onTask,
  onSubtask,
  onAgenda,
  onChatRead,
  onStatus,
  // Phase 30 (C/F) — every additional event type registers through one
  // name→handler map (party, te, inv, case, cc, config, access, reservation,
  // place, schedule, cdd, interest, ccdt, bulkmail, billedEmails, prefs,
  // deploy). Unknown names are simply never fired by the server; older
  // servers are forward-compatible.
  handlers = {},
  // C84 — optional second timer host (the pop-out window). Re-read every
  // arm cycle so open/close transitions are picked up automatically.
  getTimerHost,
}: StartLiveStreamOpts = {}) {
  let es: EventSource | null = null;
  let stopped = false;
  let lastEventId = 0;
  let backoff = 1000;
  let lastBeat = Date.now();
  let reconn: any = null; // { h, id } — timer ids are PER-WINDOW; keep the host
  let wdMain: any = null;
  let wdHost: any = null; // { h, id }

  const mainWin = typeof window !== "undefined" ? window : globalThis;
  const hostWin = () => {
    try {
      const h = getTimerHost && getTimerHost();
      if (h && !h.closed && typeof h.setTimeout === "function") return h;
    } catch {
      /* a dying pop-out window can throw on access — fall through */
    }
    return mainWin;
  };

  const clearReconn = () => {
    if (reconn) {
      try {
        reconn.h.clearTimeout(reconn.id);
      } catch {}
      reconn = null;
    }
  };

  const schedule = () => {
    if (stopped) return;
    const wait = Math.min(backoff, 30000);
    backoff = Math.min(backoff * 2, 30000);
    clearReconn();
    const h = hostWin();
    reconn = { h, id: h.setTimeout(open, wait) };
  };

  // C84 — idempotent stall check, re-armed on both hosts. Double-fire is
  // harmless (the second pass sees a fresh lastBeat / a live es).
  const checkStall = () => {
    if (stopped) return;
    if (es && Date.now() - lastBeat > STALL_MS) {
      try {
        es.close();
      } catch {}
      es = null;
      onStatus?.("reconnecting");
      backoff = 1000;
      open();
    } else {
      armWatchdog();
    }
  };
  const armWatchdog = () => {
    if (stopped) return;
    try {
      mainWin.clearTimeout(wdMain);
    } catch {}
    wdMain = mainWin.setTimeout(checkStall, WATCH_EVERY_MS);
    if (wdHost) {
      try {
        wdHost.h.clearTimeout(wdHost.id);
      } catch {}
      wdHost = null;
    }
    const h = hostWin();
    if (h !== mainWin) {
      try {
        wdHost = { h, id: h.setTimeout(checkStall, WATCH_EVERY_MS) };
      } catch {
        wdHost = null;
      }
    }
  };

  const open = async () => {
    if (stopped) return;
    let ticket;
    try {
      const r = (await mintStreamTicket()) as { ticket?: string } | null;
      ticket = r && r.ticket;
    } catch {
      // No live JWT (logged out / expired) → back off and retry.
      onStatus?.("auth-failed");
      schedule();
      return;
    }
    if (!ticket || stopped) return;
    const url =
      `${API_URL}/stream?ticket=${encodeURIComponent(ticket)}` +
      (lastEventId ? `&lastEventId=${lastEventId}` : "");
    es = new EventSource(url);
    lastBeat = Date.now();
    es.addEventListener("open", () => {
      backoff = 1000;
      lastBeat = Date.now();
      onStatus?.("open");
    });
    const on = (name: string, cb?: StreamHandler) => {
      es!.addEventListener(name, (ev: any) => {
        lastBeat = Date.now();
        if (ev.lastEventId) lastEventId = Number(ev.lastEventId) || lastEventId;
        try { cb?.(JSON.parse(ev.data)); } catch { /* ignore */ }
      });
    };
    // C84 — liveness signal only; no payload, no ring id.
    es.addEventListener("hb", () => {
      lastBeat = Date.now();
    });
    on("chat", onChat);
    on("notification", onNotification);
    on("task", onTask);
    on("subtask", onSubtask);
    on("agenda", onAgenda);
    on("chatRead", onChatRead);
    for (const [name, cb] of Object.entries(handlers)) on(name, cb);
    es.addEventListener("error", () => {
      // Ticket is consumed; close and re-mint rather than let EventSource retry.
      try { es && es.close(); } catch { /* ignore */ }
      es = null;
      onStatus?.("reconnecting");
      schedule();
    });
    armWatchdog();
  };

  open();
  armWatchdog();

  return () => {
    stopped = true;
    clearReconn();
    try {
      mainWin.clearTimeout(wdMain);
    } catch {}
    if (wdHost) {
      try {
        wdHost.h.clearTimeout(wdHost.id);
      } catch {}
    }
    if (es) { try { es.close(); } catch { /* ignore */ } es = null; }
  };
}
